import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, TrendingUp, Home, ShoppingCart, Car, Zap, Heart, DollarSign, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { setCanonical, setPageTitle, setMetaDescription, generateFAQSchema, generateBreadcrumbSchema, generateSpeakableSchema } from "@/utils/seo";
import { calculateBreakdown, formatMoney, SALARY_LEVELS } from "@/utils/taxCalculator";
import { SponsorSidebar, InlineBanner, BottomAdBanner } from "@/components/SponsorBanners";
import FinancialDisclaimer from "@/components/FinancialDisclaimer";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityPage({ citySlug }) {
  const params = useParams();
  const slug = citySlug || params.citySlug;
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios.get(`${API}/cities/${slug}`).then(r => {
      setCity(r.data);
      setCanonical(`/cost-of-living-in-${slug}`);
      setPageTitle(`Cost of Living in ${r.data.name}, ${r.data.state_code}`);
      setMetaDescription(`Explore the cost of living in ${r.data.name}, ${r.data.state_code}. Median rent: ${formatMoney(r.data.rent_1br)}/mo. COL Index: ${r.data.col_index}. See salary breakdowns and affordability.`);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-main py-24 text-center text-slate-400">Loading...</div>;
  if (!city) return <div className="container-main py-24 text-center"><h1 className="text-2xl font-bold text-slate-900">City Not Found</h1><Link to="/" className="text-emerald-600 mt-4 inline-block">Back to Calculator</Link></div>;

  const expenses = [
    { icon: Home, label: "Rent (1BR)", value: city.rent_1br, color: "bg-emerald-500" },
    { icon: ShoppingCart, label: "Groceries", value: city.groceries, color: "bg-blue-500" },
    { icon: Car, label: "Transportation", value: city.transportation, color: "bg-amber-500" },
    { icon: Zap, label: "Utilities", value: city.utilities, color: "bg-purple-500" },
    { icon: Heart, label: "Healthcare", value: city.healthcare, color: "bg-red-500" },
  ];
  const totalMonthly = expenses.reduce((a, e) => a + e.value, 0);

  const faqs = [
    { question: `What is the cost of living index in ${city.name}?`, answer: `${city.name} has a cost of living index of ${city.col_index}, where 100 is the national average. ${city.col_index > 100 ? 'This means it is more expensive than average.' : 'This means it is more affordable than average.'}` },
    { question: `What is the average rent in ${city.name}?`, answer: `The median rent for a 1-bedroom apartment in ${city.name} is ${formatMoney(city.rent_1br)}/month. A 2-bedroom averages ${formatMoney(city.rent_2br)}/month.` },
    { question: `Is ${city.name} a good place to live?`, answer: `${city.name} has a population of ${city.population?.toLocaleString()} and a median household income of ${formatMoney(city.median_household_income)}. ${city.state_tax_rate === 0 ? `${city.state_code} has no state income tax, which is a significant advantage.` : `The state income tax rate is ${(city.state_tax_rate * 100).toFixed(1)}%.`}` },
    { question: `What salary do I need to live comfortably in ${city.name}?`, answer: `Based on our analysis, you'd need roughly ${formatMoney(totalMonthly * 12 / 0.5)} annually (pre-tax) to live comfortably in ${city.name}, following the 50/30/20 budget rule.` },
  ];

  return (
    <div data-testid="city-page">
      <script type="application/ld+json">{JSON.stringify(generateFAQSchema(faqs))}</script>
      <script type="application/ld+json">{JSON.stringify(generateBreadcrumbSchema([
        { name: "Home", url: "/" }, { name: city.name }
      ]))}</script>
      <script type="application/ld+json">{JSON.stringify(generateSpeakableSchema(`/cost-of-living-in-${slug}`, [`Cost of Living in ${city.name}`], [`Complete cost breakdown for ${city.name}, ${city.state_code}. Median rent: ${formatMoney(city.rent_1br)}/month. Cost of living index: ${city.col_index}.`]))}</script>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100 py-3">
        <div className="container-main flex items-center gap-2 text-xs text-slate-500">
          <Link to="/" className="hover:text-slate-700 no-underline">Home</Link>
          <span>/</span>
          <span className="text-slate-700">Cost of Living in {city.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="hero-pattern py-12 md:py-16">
        <div className="container-main">
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-slate-500">{city.state}</span>
          </div>
          <h1 className="speakable-headline text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3">
            Cost of Living in {city.name}
          </h1>
          <p className="speakable-summary text-base text-slate-500 max-w-2xl mb-6">
            Complete cost breakdown, salary comparisons, and affordability analysis for {city.name}, {city.state_code}.
          </p>
          <div className="flex flex-wrap gap-3">
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 px-3 py-1">COL Index: {city.col_index}</Badge>
            <Badge variant="secondary" className="px-3 py-1">Population: {city.population?.toLocaleString()}</Badge>
            {city.state_tax_rate === 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-200 px-3 py-1">No State Income Tax</Badge>}
          </div>
        </div>
      </section>

      {/* Cost Breakdown */}
      <section className="py-12 md:py-16">
        <div className="container-main">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">Monthly Cost Breakdown</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {expenses.map((e, i) => (
                <div key={i} className="bg-white border border-slate-100 rounded-xl p-4 card-hover">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${e.color} bg-opacity-10 flex items-center justify-center`}>
                        <e.icon className="w-5 h-5 text-slate-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{e.label}</span>
                    </div>
                    <span className="text-lg font-bold font-mono text-slate-900">{formatMoney(e.value)}</span>
                  </div>
                  <Progress value={(e.value / city.rent_1br) * 100} className="h-2" />
                </div>
              ))}
              <div className="border-t border-slate-200 pt-4 flex justify-between items-center">
                <span className="font-semibold text-slate-900">Total Monthly</span>
                <span className="text-xl font-bold font-mono text-slate-900">{formatMoney(totalMonthly)}</span>
              </div>
            </div>
            <div>
              <Card className="bg-slate-50 border-slate-100 mb-4">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-1">Median Home Price</h3>
                  <p className="text-2xl font-bold font-mono text-slate-900 mb-4">{formatMoney(city.home_price)}</p>
                  <h3 className="font-semibold text-slate-900 mb-1">Median Household Income</h3>
                  <p className="text-2xl font-bold font-mono text-slate-900 mb-4">{formatMoney(city.median_household_income)}</p>
                  <h3 className="font-semibold text-slate-900 mb-1">State Tax Rate</h3>
                  <p className="text-2xl font-bold font-mono text-slate-900">
                    {city.state_tax_rate === 0 ? "None" : `${(city.state_tax_rate * 100).toFixed(1)}%`}
                    {city.city_tax_rate > 0 && <span className="text-sm text-slate-500 ml-2">+ {(city.city_tax_rate * 100).toFixed(1)}% city tax</span>}
                  </p>
                </CardContent>
              </Card>
              {/* AFFILIATE SIDEBAR - Money maker */}
              <SponsorSidebar pageType="city" cityName={city.name} />
            </div>
          </div>
        </div>
      </section>

      {/* Housing affiliate banner */}
      <div className="container-main">
        {/* Apartment Affiliate Banner */}
        <a href="https://www.hellolanding.com/r/web/2vvg1H" target="_blank" rel="noopener" className="no-underline block">
          <div className="p-4 bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 border border-blue-200 rounded-xl my-8 flex items-center gap-4 hover:shadow-md transition-all" data-testid="post-result-banner">
            <img src="https://images.pexels.com/photos/19899060/pexels-photo-19899060.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop" alt="Furnished apartment" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
            <div className="flex-1">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Sponsored</p>
              <p className="text-sm font-semibold text-slate-900">Moving to {city.name}? Get $250 Off Your First Furnished Apartment</p>
              <p className="text-xs text-slate-500">Move freely, stay comfortably. Furnished apartments in 200+ cities. *Limited time offer. Terms apply.</p>
            </div>
            <span className="text-xs font-bold text-blue-600 whitespace-nowrap hidden md:inline">Claim $250 Off &rarr;</span>
          </div>
        </a>
        {/* Mobile-Only Ads */}
        <div className="lg:hidden my-6">
          <div className="flex justify-center">
            <a href="https://creditcane.com" target="_blank" rel="noopener" className="no-underline block">
              <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-sky-50 via-cyan-50 to-sky-100 border-2 border-sky-200 rounded-xl hover:shadow-md transition-all">
                <DollarSign className="w-4 h-4 text-sky-700 flex-shrink-0" />
                <p className="text-sm font-semibold text-sky-900">Bad Credit? We Can Help</p>
                <span className="text-xs font-semibold text-sky-700 whitespace-nowrap">Fix Credit &rarr;</span>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Salary Table */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container-main">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6">Salary Breakdown in {city.name}</h2>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="salary-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Salary</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Monthly Take-Home</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">After Expenses</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Verdict</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {SALARY_LEVELS.map(sal => {
                  const b = calculateBreakdown(sal, city);
                  const vc = b.verdict === "Comfortable" ? "text-emerald-700 bg-emerald-50" : b.verdict === "Tight" ? "text-amber-700 bg-amber-50" : "text-red-700 bg-red-50";
                  return (
                    <tr key={sal} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-mono font-medium">{formatMoney(sal)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(b.net_monthly)}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatMoney(b.remaining_monthly)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${vc}`}>{b.verdict}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/${sal/1000}k-salary-in-${city.slug}`} className="text-emerald-600 hover:text-emerald-700 text-xs font-medium no-underline">
                          Details <ArrowRight className="w-3 h-3 inline" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <div className="container-main max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl px-4">
                <AccordionTrigger className="text-sm font-medium text-slate-800 hover:no-underline py-4">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 pb-4">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-emerald-50 border-t border-emerald-100">
        <div className="container-main text-center">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Want to see how {city.name} compares?</h2>
          <p className="text-sm text-slate-500 mb-4">Compare {city.name} with another city side-by-side.</p>
          <Link to={`/compare`}>
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="compare-cta">Compare Cities</Button>
          </Link>
        </div>
      </section>

      <div className="container-main"><FinancialDisclaimer /></div>
      <BottomAdBanner />
    </div>
  );
}
